import { getCurrentWorkflowStep } from "@/utils/stepRole";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import { developmentApi } from "@/lib/api";
import { DevelopmentTimeline } from "@/pages/Developments/DevelopmentTimeline";
import { DevelopmentChatFeed } from "@/pages/Developments/DevelopmentChatFeed";
import { DeliverablesPanel } from "@/pages/Developments/DeliverablesPanel";
import { Badge, type BadgeVariant } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { ChangeStatusModal } from "@/pages/Developments/ChangeStatusModal";
import { ChangePriorityModal } from "@/pages/Developments/ChangePriorityModal";
import { ChangeAssigneeModal } from "@/pages/Developments/ChangeAssigneeModal";
import { date } from "@/utils/format";
import { PageHeader } from "@/components/PageHeader";
import { DevelopmentInfo } from "@/pages/Developments/DevelopmentInfo";

export interface DevelopmentDetailItem {
    id: number;
    code: string;
    title: string;
    description: string;
    status: string;
    priority: string;
    client_id?: number;
    assigned_id?: number;
    design_url?: string | null;
    figma_url?: string | null;
    demo_url?: string | null;
    production_url?: string | null;
    current_step?: string | null;
    has_client_activity?: boolean;
    can_delete?: boolean;
    client?: { id: number; name: string; email?: string };
    assigned?: { id: number; name: string };
    created_at: string;
    updated_at: string;
    subcategories?: Array<{ id: number; name: string }>;
    attachments?: Array<{
        id: number;
        user_id?: number;
        name: string;
        file_size: number;
        mime_type?: string;
        category?: string | null;
        is_internal?: boolean;
        message_id?: number | null;
        created_at?: string;
        user?: {
            id: number;
            name: string;
            email?: string;
            roles?: Array<{ name: string }>;
        };
    }>;
    messages?: Array<{
        id: number;
        user_id?: number;
        sender_role?: string;
        sender_name?: string;
        type?: string;
        content?: string;
        is_internal?: boolean;
        metadata?: Record<string, unknown>;
        user?: {
            id: number;
            name: string;
            email?: string;
            roles?: Array<{ name: string }>;
        };
    }>;
    participants?: Array<{
        id: number;
        user_id: number;
        role: "viewer" | "editor";
        user?: { id: number; name: string; email: string };
    }>;
    steps?: Array<{
        step_key: string;
        step_order: number;
        signed_by?: number | null;
        signed_at?: string | null;
        status: string;
    }>;
    tickets?: Array<{
        key: string;
        title?: string;
        url?: string;
        platform?: string;
    }>;
    metadata?: {
        tickets?: Array<{ key: string; title?: string }>;
        jira_tasks?: Array<{ key: string; title?: string }>;
        tasks?: Array<{ key: string; title?: string }>;
        [key: string]: unknown;
    };
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
    request: "info",
    planning: "secondary",
    development: "warning",
    testing: "neutral",
    completed: "success",
    cancelled: "danger",
};

const PRIORITY_CONFIG: Record<
    string,
    { arrows: number; color: string; label: string }
> = {
    low: {
        arrows: 1,
        color: "text-slate-400 dark:text-zinc-400",
        label: "text-slate-500 dark:text-zinc-400",
        bg: "neutral",
    },
    medium: {
        arrows: 2,
        color: "text-blue-500 dark:text-blue-400",
        label: "text-blue-600 dark:text-blue-400",
        bg: "info",
    },
    high: {
        arrows: 3,
        color: "text-amber-500 dark:text-amber-400",
        label: "text-amber-600 dark:text-amber-400",
        bg: "warning",
    },
    urgent: {
        arrows: 4,
        color: "text-rose-500 dark:text-rose-400",
        label: "text-rose-600 dark:text-rose-400",
        bg: "danger",
    },
};

interface DevelopmentDetailProps {
    developmentId?: string;
}

export const DevelopmentDetail: React.FC<DevelopmentDetailProps> = ({
    developmentId,
}) => {
    const { t } = useTranslation("global");
    const { id: routeId } = useParams<{ id: string }>();
    const id = developmentId ?? routeId;
    const { isIt } = useAuth();
    const navigate = useNavigate();

    const [item, setItem] = useState<DevelopmentDetailItem | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isResetting, setIsResetting] = useState<boolean>(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false);
    const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
    const [copiedCode, setCopiedCode] = useState<boolean>(false);
    const [targetMessage, setTargetMessage] = useState<{
        id: number;
        timestamp: number;
    } | null>(null);
    const [isRightPanelOpen, setIsRightPanelOpen] = useState<boolean>(true);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
    const [openPriorityModal, setOpenPriorityModal] = useState<boolean>(false);
    const [openAssigneeModal, setOpenAssigneeModal] = useState<boolean>(false);
    const [openDesignModalTrigger, setOpenDesignModalTrigger] = useState<{
        timestamp: number;
        requirementDocId?: number;
    } | null>(null);

    const handleSelectStep = (step: {
        step_key: string;
        title: string;
    }): void => {
        if (!item?.messages || item.messages.length === 0) {
            const chatEl = document.getElementById("chat-feed-container");
            chatEl?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        const steps = item.steps || [];
        const stepIndex = steps.findIndex(
            (s) => s.step_key === step.step_key || s.tag === step.step_key
        );

        let matched;

        if (stepIndex <= 0) {
            // First step (or not found), go to the very first message
            matched = item.messages[0];
        } else {
            const prevStep = steps[stepIndex - 1];
            // Iterate from the most recent messages backwards to find the last time this phase was entered
            const reversedMessages = [...item.messages].reverse();

            matched = reversedMessages.find((m) => {
                if (m.type === "step_event") {
                    const meta = m.metadata as
                        | Record<string, unknown>
                        | undefined;
                    // Forward transition to this step: previous step was completed
                    if (
                        meta?.auto === "step_completed" &&
                        (meta?.step_tag === prevStep.step_key ||
                            ((prevStep as any).tag &&
                                meta?.step_tag === (prevStep as any).tag))
                    ) {
                        return true;
                    }
                    // Backward transition to this step: another step was abandoned to reopen this one
                    if (
                        meta?.auto === "step_reopened" &&
                        (meta?.target_step_tag === step.step_key ||
                            ((step as any).tag &&
                                meta?.target_step_tag === (step as any).tag))
                    ) {
                        return true;
                    }
                }
                return false;
            });

            // Fallback if no specific entry event is found
            if (!matched) {
                matched = reversedMessages.find((m) => {
                    const meta = m.metadata as
                        | Record<string, unknown>
                        | undefined;
                    return (
                        meta?.step_key === step.step_key ||
                        meta?.step_tag === step.step_key ||
                        meta?.target_step_tag === step.step_key
                    );
                });
            }
        }

        if (matched) {
            setTargetMessage({ id: matched.id, timestamp: Date.now() });
        } else {
            const chatEl = document.getElementById("chat-feed-container");
            chatEl?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    const fetchDevelopment = async (background = false): Promise<void> => {
        if (!id) {
            return;
        }
        if (!background) setLoading(true);
        try {
            const res = await developmentApi.get(Number(id));
            if (res.result) {
                if (background) {
                    const scrollY = window.scrollY;
                    setItem(res.data);
                    requestAnimationFrame(() => {
                        window.scrollTo({ top: scrollY, behavior: "instant" });
                    });
                } else {
                    setItem(res.data);
                }
            } else {
                setError(res.message || "Desarrollo no encontrado");
            }
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Error cargando desarrollo"
            );
        } finally {
            if (!background) setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevelopment();
    }, [id]);

    const hasClientMessages = useMemo(() => {
        if (!item?.messages || !Array.isArray(item.messages)) {
            return false;
        }

        return item.messages.some((msg) => {
            const isClientUser =
                msg.sender_role === "client" ||
                (msg.user_id &&
                    item.client_id &&
                    msg.user_id === item.client_id) ||
                Boolean(
                    msg.user?.roles?.some(
                        (r) => r.name === "client" || r.name === "cliente"
                    )
                );

            if (!isClientUser) {
                return false;
            }

            if (
                msg.content &&
                msg.content.startsWith("Solicitud de desarrollo creada:")
            ) {
                return false;
            }

            return true;
        });
    }, [item]);

    const hasClientAttachments = useMemo(() => {
        if (!item?.attachments || !Array.isArray(item.attachments)) {
            return false;
        }

        return item.attachments.some((att) => {
            if (att.is_internal === false) {
                return true;
            }
            if (
                att.user_id &&
                item.client_id &&
                att.user_id === item.client_id
            ) {
                return true;
            }
            return Boolean(
                att.user?.roles?.some(
                    (r) => r.name === "client" || r.name === "cliente"
                )
            );
        });
    }, [item]);

    const hasClientActivity = useMemo(() => {
        if (typeof item?.has_client_activity === "boolean") {
            return item.has_client_activity;
        }

        return hasClientMessages || hasClientAttachments;
    }, [item, hasClientMessages, hasClientAttachments]);

    const isInPhaseOne = useMemo(() => {
        if (typeof item?.is_in_phase_one === "boolean") {
            return item.is_in_phase_one;
        }

        const hasCompletedSteps =
            item?.steps?.some((s) => s.is_completed) ?? false;
        if (hasCompletedSteps) {
            return false;
        }

        const firstStep = item?.steps?.[0];
        if (
            firstStep &&
            item?.step_id &&
            item.step_id !== firstStep.development_step_id &&
            item.step_id !== firstStep.id
        ) {
            return false;
        }

        return true;
    }, [item]);

    const isInDesignPhase = useMemo(() => {
        return getCurrentWorkflowStep(item?.steps ?? [])?.tag === "design";
    }, [item]);

    const isInFrontendPhase = useMemo(() => {
        return (
            getCurrentWorkflowStep(item?.steps ?? [])
                ?.tag?.toLowerCase()
                .includes("frontend") ?? false
        );
    }, [item]);

    const isInBackendPhase = useMemo(() => {
        return (
            getCurrentWorkflowStep(item?.steps ?? [])
                ?.tag?.toLowerCase()
                .includes("backend") ?? false
        );
    }, [item]);

    const canDelete = useMemo(() => {
        if (typeof item?.can_delete === "boolean") {
            return item.can_delete;
        }

        return isIt && isInPhaseOne && !hasClientActivity;
    }, [item, isIt, isInPhaseOne, hasClientActivity]);

    const handleConfirmDelete = async (): Promise<void> => {
        if (!id || !canDelete) {
            return;
        }

        setIsDeleting(true);

        try {
            const res = await developmentApi.delete(Number(id));
            if (res.result) {
                setIsDeleteModalOpen(false);
                navigate("/developments");
            } else {
                alert(res.message || "Error al eliminar");
            }
        } catch (err) {
            alert(
                "Error al eliminar: " +
                    (err instanceof Error ? err.message : "Error")
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const handleReset = async (): Promise<void> => {
        if (!id) {
            return;
        }

        setIsResetting(true);
        try {
            const res = await developmentApi.reset(Number(id));
            if (res.result) {
                setIsResetModalOpen(false);
                window.location.reload();
            } else {
                alert(res.message || "Error al resetear");
            }
        } catch (err) {
            alert(
                "Error al resetear: " +
                    (err instanceof Error ? err.message : "Error")
            );
        } finally {
            setIsResetting(false);
        }
    };

    if (loading) {
        return (
            <div className="p-16 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary mb-3" />
                <p className="text-slate-500 font-medium text-sm">
                    {t("developments.loading")}
                </p>
            </div>
        );
    }

    if (error || !item) {
        return (
            <div className="bg-white rounded-xl border border-red-200 p-8 text-center max-w-lg mx-auto mt-8">
                <p className="text-red-600 font-semibold mb-4">
                    {error || t("developments.detail.not_found")}
                </p>
                <Button
                    variant="primary"
                    onClick={() => navigate("/developments")}
                    icon={<Icon value="back" size={16} />}
                >
                    {t("common.back")}
                </Button>
            </div>
        );
    }

    const metaWidth = "max-w-5xl mx-auto w-full";
    const workspaceWidth = "mx-auto w-full"; //max-w-[1480px]

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader
                compact
                className="!mb-0"
                title={
                    <div className="flex items-center gap-2">
                        <span>
                            {item.code} - {item.title}
                        </span>
                        <button
                            onClick={() => setIsInfoModalOpen(true)}
                            className="text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <Icon value="info" className="mb-1" size={24} />
                        </button>
                    </div>
                }
                subtitle={
                    item.subcategories &&
                    item.subcategories.length > 0 && (
                        <div className="">
                            {item.subcategories.map((sub) => {
                                const subWithParent = sub as {
                                    id: number;
                                    name: string;
                                    category?: {
                                        id: number;
                                        name: string;
                                    } | null;
                                };
                                return (
                                    <div className="flex items-center gap-1">
                                        <Icon
                                            value="tag"
                                            size={14}
                                            className="text-slate-500 dark:text-zinc-400 shrink-0"
                                        />
                                        {subWithParent.category ? (
                                            <span className="flex items-center gap-1">
                                                <span className="opacity-70">
                                                    {
                                                        subWithParent.category
                                                            .name
                                                    }
                                                </span>
                                                <Icon
                                                    value="chevronRight"
                                                    size={10}
                                                    className="opacity-50 shrink-0"
                                                />
                                                <span>{sub.name}</span>
                                            </span>
                                        ) : (
                                            <span>{sub.name}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )
                }
                action={{
                    label: t("common.back"),
                    icon: "arrowLeft",
                    onClick: () => navigate("/developments"),
                }}
            />

            <div className="box">
                {/*           
                <div className='text-center text-sm font-semibold text-slate-700 dark:text-zinc-300 border-b border-slate-100 mb-4'>
                    {t('developments.detail.description')}
                </div>                
                */}
                <div className="">{item.description}</div>
            </div>

            {/* Stepper / Timeline Workflow */}
            <DevelopmentTimeline
                className={metaWidth}
                development={item}
                onUpdate={(data) =>
                    setItem(data as unknown as DevelopmentDetailItem)
                }
                onSelectStep={handleSelectStep}
                onOpenDesignModal={() =>
                    setOpenDesignModalTrigger({ timestamp: Date.now() })
                }
            />

            {/* Main Split Content: Chat Feed + Deliverables */}
            <div className={workspaceWidth}>
                {/* Mobile: panel above chat */}
                <div className="mb-6 lg:hidden">
                    <DeliverablesPanel
                        development={item}
                        onUpdate={setItem}
                        onRefresh={() => fetchDevelopment(true)}
                        onNavigateToMessage={(messageId) =>
                            setTargetMessage({
                                id: messageId,
                                timestamp: Date.now(),
                            })
                        }
                        isInPhaseOne={isInPhaseOne}
                        isInDesignPhase={isInDesignPhase}
                        isInFrontendPhase={isInFrontendPhase}
                        isInBackendPhase={isInBackendPhase}
                        onOpenDesignModal={(requirementDocId) =>
                            setOpenDesignModalTrigger({
                                timestamp: Date.now(),
                                requirementDocId,
                            })
                        }
                    />
                </div>

                <div className="flex flex-col lg:flex-row lg:items-stretch gap-6 overflow-visible">
                    <div className="min-w-0 flex-1 w-full overflow-visible flex flex-col min-h-[520px] sm:min-h-[640px] lg:min-h-[700px]">
                        <DevelopmentChatFeed
                            development={item}
                            onUpdate={(data) =>
                                setItem(
                                    data as unknown as DevelopmentDetailItem
                                )
                            }
                            targetMessage={targetMessage}
                            isRightPanelOpen={isRightPanelOpen}
                            onToggleRightPanel={() =>
                                setIsRightPanelOpen((open) => !open)
                            }
                            showRightPanelToggle
                            openDesignModalTrigger={openDesignModalTrigger}
                        />
                    </div>

                    {/* Desktop: panel fixed on the right */}
                    <aside
                        className={`hidden shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out lg:block ${
                            isRightPanelOpen ? "w-[360px]" : "w-0 max-h-0"
                        }`}
                    >
                        <div
                            className={`w-[360px] transition-[opacity,transform] duration-300 ease-in-out ${
                                isRightPanelOpen
                                    ? "translate-x-0 opacity-100"
                                    : "pointer-events-none translate-x-4 opacity-0"
                            }`}
                            aria-hidden={!isRightPanelOpen}
                        >
                            <DeliverablesPanel
                                development={item}
                                onUpdate={setItem}
                                onRefresh={() => fetchDevelopment(true)}
                                onNavigateToMessage={(messageId) =>
                                    setTargetMessage({
                                        id: messageId,
                                        timestamp: Date.now(),
                                    })
                                }
                                isInPhaseOne={isInPhaseOne}
                                isInDesignPhase={isInDesignPhase}
                                isInFrontendPhase={isInFrontendPhase}
                                isInBackendPhase={isInBackendPhase}
                                onOpenDesignModal={(requirementDocId) =>
                                    setOpenDesignModalTrigger({
                                        timestamp: Date.now(),
                                        requirementDocId,
                                    })
                                }
                            />
                        </div>
                    </aside>
                </div>
            </div>

            {/* Delete Development Confirmation / Blocked Modal */}
            {isIt && (
                <Modal
                    isOpen={isDeleteModalOpen}
                    onClose={() => {
                        if (!isDeleting) {
                            setIsDeleteModalOpen(false);
                        }
                    }}
                    title={
                        canDelete
                            ? t("developments.modal_delete.title")
                            : t("developments.modal_delete_blocked.title")
                    }
                    maxWidth="sm"
                    submitVariant={canDelete ? "danger" : undefined}
                    onSubmit={canDelete ? handleConfirmDelete : undefined}
                    isSubmitting={isDeleting}
                    submitLabel={canDelete ? t("modal.delete") : undefined}
                    submittingLabel={t("modal.deleting")}
                    cancelLabel={
                        canDelete ? t("modal.cancel") : t("modal.close")
                    }
                >
                    {!canDelete ? (
                        <div className="flex items-start gap-3.5 py-2">
                            <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-200 dark:border-amber-900/50">
                                <Icon value="alertTriangle" size={20} />
                            </div>
                            <div className="space-y-2.5 min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <Badge
                                        variant="neutral"
                                        size="md"
                                        pill
                                        className="font-mono font-bold text-xs px-3 py-0.5 rounded-full bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-zinc-200 border border-slate-300/80 dark:border-white/15 shadow-2xs"
                                    >
                                        {item.code}
                                    </Badge>
                                    <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                                        {item.title}
                                    </p>
                                </div>
                                <p className="text-xs text-slate-600 dark:text-zinc-300 leading-relaxed">
                                    {t(
                                        "developments.modal_delete_blocked.desc"
                                    )}
                                </p>
                                <div className="pt-1 flex flex-col gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                                    {!isInPhaseOne && (
                                        <div className="flex items-center gap-1.5">
                                            <Icon
                                                value="clock"
                                                size={13}
                                                className="shrink-0"
                                            />
                                            <span>
                                                {t(
                                                    "developments.modal_delete_blocked.reason_phase"
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    {hasClientMessages && (
                                        <div className="flex items-center gap-1.5">
                                            <Icon
                                                value="message"
                                                size={13}
                                                className="shrink-0"
                                            />
                                            <span>
                                                {t(
                                                    "developments.modal_delete_blocked.reason_messages"
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    {hasClientAttachments && (
                                        <div className="flex items-center gap-1.5">
                                            <Icon
                                                value="paperclip"
                                                size={13}
                                                className="shrink-0"
                                            />
                                            <span>
                                                {t(
                                                    "developments.modal_delete_blocked.reason_attachments"
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 py-1">
                            <p className="text-center text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                                {t("developments.modal_delete.desc")}
                            </p>

                            <div className="text-center p-3.5 bg-slate-50 dark:bg-dark-subtle/60 rounded-xl border border-slate-200/80 dark:border-white/10 space-y-2">
                                <div>
                                    <Badge
                                        variant="neutral"
                                        size="md"
                                        pill
                                        className="font-mono font-bold text-xs px-3 py-1 rounded-full bg-white dark:bg-white/10 text-slate-700 dark:text-zinc-200 border border-slate-300/80 dark:border-white/15 shadow-2xs"
                                    >
                                        {item.code}
                                    </Badge>
                                </div>

                                <div className="text-sm font-bold text-slate-900 dark:text-white">
                                    {item.title}
                                </div>

                                {item.description && (
                                    <div className="text-xs text-slate-500 dark:text-zinc-400 whitespace-pre-line leading-relaxed max-h-36 overflow-y-auto">
                                        {item.description}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Modal>
            )}
            {/* TESTING ONLY: Reset Confirmation Modal */}
            {isIt && (
                <Modal
                    isOpen={isResetModalOpen}
                    onClose={() => {
                        if (!isResetting) {
                            setIsResetModalOpen(false);
                        }
                    }}
                    title="⚠️ RESET desarrollo (testing)"
                    maxWidth="sm"
                    submitVariant="warning"
                    onSubmit={handleReset}
                    isSubmitting={isResetting}
                    submitLabel="Sí, resetear todo"
                    submittingLabel="Reseteando..."
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="flex items-start gap-3.5 py-2">
                        <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-200 dark:border-amber-900/50">
                            <Icon value="alertTriangle" size={20} />
                        </div>
                        <div className="space-y-2 min-w-0 flex-1">
                            <p className="text-sm font-bold text-slate-900 dark:text-white">
                                ¿Resetear "{item?.title}"?
                            </p>
                            <p className="text-xs text-slate-600 dark:text-zinc-300 leading-relaxed">
                                Esta acción eliminará{" "}
                                <strong>todos los mensajes</strong> (excepto el
                                inicial), adjuntos, progreso de fases y
                                participantes. El desarrollo volverá al estado
                                inicial.
                            </p>
                            <p className="text-xs text-amber-700 dark:text-amber-300 font-semibold">
                                Solo visible para IT. Eliminar este botón antes
                                de producción.
                            </p>
                        </div>
                    </div>
                </Modal>
            )}

            <ChangeStatusModal
                isOpen={openStatusModal}
                onClose={() => setOpenStatusModal(false)}
                target={
                    item
                        ? {
                              id: item.id,
                              status: item.status,
                              title: item.title,
                              code: item.code,
                          }
                        : null
                }
                onSuccess={(newStatus) => {
                    setOpenStatusModal(false);
                    fetchDevelopment(true);
                }}
            />

            <Modal
                isOpen={isInfoModalOpen}
                onClose={() => setIsInfoModalOpen(false)}
                //title={t('developments.detail.info_title')}
                title={item?.code}
            >
                {item && (
                    <DevelopmentInfo
                        item={item as any}
                        isIt={isIt}
                        setOpenStatusModal={setOpenStatusModal}
                        setOpenPriorityModal={setOpenPriorityModal}
                        setOpenAssigneeModal={setOpenAssigneeModal}
                    />
                )}
            </Modal>

            <ChangePriorityModal
                isOpen={openPriorityModal}
                onClose={() => setOpenPriorityModal(false)}
                development={item as any}
                onSuccess={(updated) => {
                    setOpenPriorityModal(false);
                    fetchDevelopment(true);
                }}
            />

            <ChangeAssigneeModal
                isOpen={openAssigneeModal}
                onClose={() => setOpenAssigneeModal(false)}
                development={item as any}
                onSuccess={(updated) => {
                    setOpenAssigneeModal(false);
                    fetchDevelopment(true);
                }}
            />
        </div>
    );
};
